import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import {
    Container,
    Paper,
    Typography,
    CircularProgress,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import GoalSection from '../components/GoalSection';
import NeedsSection from '../components/NeedsSection';
import ValuesSection from '../components/ValuesSection';
import ResourcesSection from '../components/ResourcesSection';
import SupportingThoughtsSection from '../components/SupportingThoughtsSection';
import EcologySection from '../components/EcologySection';
import ActionPlanSection from '../components/ActionPlanSection';
import PDFGenerator from '../components/PDFGenerator';
import { trackChatbotSession, trackPDFUnlockChoice } from '../utils/api';

const sections = [
    {
        title: "Cel",
        component: GoalSection
    },
    {
        title: "Potrzeby",
        component: NeedsSection
    },
    {
        title: "Wartości",
        component: ValuesSection
    },
    {
        title: "Zasoby",
        component: ResourcesSection
    },
    {
        title: "Wspierające myśli",
        component: SupportingThoughtsSection
    },
    {
        title: "Wpływ na Ciebie i Twoje otoczenie",
        component: EcologySection
    },
    {
        title: "Plan działania",
        component: ActionPlanSection
    }
];

const Chatbot = () => {
    const navigate = useNavigate();
    const { formData, updateFormData, currentSection, setCurrentSection, resetForm } = useFormContext();
    const [isLoading, setIsLoading] = useState(true);
    const [showResetModal, setShowResetModal] = useState(false);
    const hasTrackedStart = useRef(false);

    useEffect(() => {
        // Give localStorage time to load
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 100);

        return () => clearTimeout(timer);
    }, [navigate]);

    // Track session start (only once)
    useEffect(() => {
        if (!hasTrackedStart.current) {
            hasTrackedStart.current = true;
            trackChatbotSession('start', { timestamp: new Date().toISOString() })
                .then(response => {
                    // Save userId if returned
                    if (response.userId) {
                        localStorage.setItem('userId', response.userId);
                    }
                })
                .catch(error => {
                    console.error('Failed to track chatbot session start:', error);
                });
        }
    }, []);

    // Track session abandonment when user leaves
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (hasTrackedStart.current && currentSection < sections.length) {
                // Track abandonment with current progress
                trackChatbotSession('abandon', {
                    timestamp: new Date().toISOString(),
                    abandonedAtStep: currentSection + 1,
                    stepName: sections[currentSection]?.title,
                    totalSteps: sections.length
                }).catch(error => {
                    console.error('Failed to track chatbot abandonment:', error);
                });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [currentSection]);

    // Show loading spinner while checking localStorage
    if (isLoading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }



    const handleSectionComplete = (data) => {
        updateFormData(data);

        // Track step completion
        const stepData = {
            stepNumber: currentSection + 1,
            stepName: sections[currentSection].title,
            timestamp: new Date().toISOString(),
            formDataKeys: Object.keys(data),
            completed: true
        };

        trackChatbotSession('step', {}, stepData).catch(error => {
            console.error('Failed to track chatbot step:', error);
        });

        if (currentSection === sections.length - 1) {
            // Track completion of the chatbot process
            trackChatbotSession('complete', {
                timestamp: new Date().toISOString(),
                totalSteps: sections.length,
                formDataKeys: Object.keys(data)
            }).catch(error => {
                console.error('Failed to track chatbot session completion:', error);
            });

            setCurrentSection(sections.length); // Move to PDF view
        } else {
            setCurrentSection(prev => prev + 1);
        }
    };

    const handleGoBack = () => {
        if (currentSection > 0) {
            setCurrentSection(prev => prev - 1);
        }
    };

    const handleReset = () => {
        setShowResetModal(true);
    };

    const handleConfirmReset = () => {
        resetForm();
        setShowResetModal(false);
    };

    const handleCancelReset = () => {
        setShowResetModal(false);
    };

    const CurrentSectionComponent = sections[currentSection]?.component;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundImage: 'url(/assets/Hero.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}
        >
            <Container maxWidth={currentSection < sections.length ? "md" : false}>
                {currentSection < sections.length ? (
                    <Paper elevation={3} sx={{ p: 4, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                <img src="/logo-192x192.png" alt="Find solution Logo" style={{ height: '32px', width: '32px', marginRight: '12px' }} />
                                <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 0 }}>
                                    Find solution - {sections[currentSection].title}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleReset}
                                size="small"
                            >
                                ZACZNIJ OD NOWA
                            </Button>
                        </Box>

                        {CurrentSectionComponent && (
                            <CurrentSectionComponent
                                onNext={handleSectionComplete}
                                onBack={handleGoBack}
                                formData={formData}
                                isFirstSection={currentSection === 0}
                            />
                        )}
                    </Paper>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, px: 2 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                    // Track reset button click from PDF section
                                    trackPDFUnlockChoice(
                                        'reset_from_pdf',
                                        'Rozpocznij od nowa',
                                        'click',
                                        {
                                            variant: 'outlined',
                                            color: 'error',
                                            size: 'small',
                                            location: 'pdf_section'
                                        }
                                    ).catch(error => {
                                        console.error('Failed to track PDF unlock choice:', error);
                                    });
                                    handleReset();
                                }}
                                size="small"
                            >
                                Rozpocznij od nowa
                            </Button>
                        </Box>
                        <PDFGenerator formData={formData} />
                    </>
                )}
            </Container>

            <Dialog
                open={showResetModal}
                onClose={handleCancelReset}
                aria-labelledby="reset-dialog-title"
                aria-describedby="reset-dialog-description"
            >
                <DialogTitle id="reset-dialog-title">
                    Potwierdzenie resetu
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="reset-dialog-description">
                        Czy na pewno chcesz zresetować formularz? Wszystkie odpowiedzi zostaną utracone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelReset} color="primary">
                        Anuluj
                    </Button>
                    <Button onClick={handleConfirmReset} color="error" variant="contained" autoFocus>
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Chatbot; 